import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitch from "components/common/LanguageSwitch";
import { ErrorToaster, successToaster } from "components/common/toast";
import handleError from "utils/handleError";
import { isAuthenticated } from "utils/authenticated";
import { loginApi } from "network/user";
import { UserContextProvider } from "context/UserContext";
import { GetMenuCardsAPI, UpdateMenuCardAPI } from "network/menuCards";
import { AddMyWineAPI, GetMyWinesAPI, RequestWineAPI, SearchDatasetWinesAPI, UpdateMyWineAPI } from "network/quickApp";

const GOLD = "#b8932f";
const INK = "#222";
const STYLE = `
.qa{min-height:100vh;background:#f5f2ea;color:${INK};padding-bottom:84px;font-family:inherit}
.qa *{box-sizing:border-box}
.qa-top{position:sticky;top:0;z-index:5;background:#fff;border-bottom:1px solid #e5dfd0;padding:12px 14px}
.qa-title{font-size:1.15rem;font-weight:700;margin:0 0 8px}
.qa-in{width:100%;height:48px;border:1px solid #cfc7b2;border-radius:12px;padding:0 14px;font-size:1rem;background:#fff;color:${INK}}
.qa-body{padding:12px 14px}
.qa-chips{display:flex;gap:8px;margin:10px 0 0;flex-wrap:wrap}
.qa-chip{border:1px solid ${GOLD};background:#fff;color:${GOLD};border-radius:20px;padding:6px 14px;font-size:.9rem}
.qa-chip.on{background:${GOLD};color:#fff}
.qa-card{background:#fff;border:1px solid #e5dfd0;border-radius:14px;padding:12px 14px;margin-bottom:10px}
.qa-card.off{opacity:.6}
.qa-row{display:flex;align-items:center;justify-content:space-between;gap:10px}
.qa-name{font-weight:600;line-height:1.25;word-break:break-word}
.qa-sub{font-size:.85rem;color:#777}
.qa-btn{min-height:46px;border:0;border-radius:12px;padding:0 16px;font-weight:600;font-size:.95rem;background:${GOLD};color:#fff}
.qa-btn.sec{background:#fff;color:${GOLD};border:1px solid ${GOLD}}
.qa-btn.red{background:#c0392b}
.qa-btn:disabled{opacity:.5}
.qa-price{border:1px dashed #cfc7b2;border-radius:10px;padding:6px 10px;background:#fff;color:${INK};font-weight:600}
.qa-tabs{position:fixed;left:0;right:0;bottom:0;z-index:6;display:flex;background:#fff;border-top:1px solid #e5dfd0}
.qa-tab{flex:1;border:0;background:#fff;padding:10px 4px 12px;color:#888;font-size:.8rem}
.qa-tab i{display:block;font-size:1.3rem;margin-bottom:2px}
.qa-tab.on{color:${GOLD};font-weight:700}
.qa-login{min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;background:#f5f2ea}
.qa-login form{width:100%;max-width:380px;background:#fff;border:1px solid #e5dfd0;border-radius:16px;padding:24px 20px}
.qa-login h1{font-size:1.3rem;font-weight:700;margin:0 0 4px}
.qa-login p{color:#777;font-size:.9rem;margin:0 0 16px}
.qa-login .qa-in{margin-bottom:12px}
.qa-lab{display:block;font-size:.85rem;color:#666;margin:10px 0 4px}
.qa-file{display:flex;align-items:center;gap:10px;padding:12px;border:2px dashed ${GOLD};border-radius:12px;color:${GOLD};font-weight:600;background:rgba(184,147,47,.08);margin:0}
.qa-file small{color:#777;font-weight:400;word-break:break-all}
.qa-empty{text-align:center;color:#888;padding:34px 10px}
.qa-lang{position:fixed;top:10px;right:12px;z-index:7;font-size:.75rem}
.qa-badge{display:inline-block;font-size:.72rem;border-radius:8px;padding:2px 8px;background:#ddd;color:#555;margin-left:6px}
`;

const euro = (n) => (n == null || n === "" ? "-" : `€ ${String(n).replace(".", ",")}`);
const vint = (v, name = "") => {
  const year = v ? parseInt(v, 10) : null;
  return year && !String(name).includes(String(year)) ? ` ${year}` : "";
};
const norm = (t) => String(t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const fail = (e) => ErrorToaster(handleError(e?.status_code, e?.message));

// ---- Vini: cerca, nascondi (finito), cambia prezzo ----
const WinesTab = ({ wines, reload, setWines }) => {
  const { t } = useTranslation("quickapp");
  const [q, setQ] = useState("");
  const [view, setView] = useState("all");
  const [editing, setEditing] = useState(null); // { uuid, value }
  const [busy, setBusy] = useState(null);

  const shown = useMemo(() => {
    const words = norm(q).split(/\s+/).filter(Boolean);
    return wines.filter((w) => {
      if (view === "on" && !w.is_available) return false;
      if (view === "off" && w.is_available) return false;
      const hay = norm(`${w.name} ${w.winery || ""} ${w.vintage || ""}`);
      return words.every((x) => hay.includes(x));
    });
  }, [wines, q, view]);

  const patch = async (w, payload, message) => {
    setBusy(w.uuid);
    try {
      const res = await UpdateMyWineAPI(w.uuid, payload);
      setWines((list) => list.map((x) => (x.uuid === w.uuid ? { ...x, ...res.data } : x)));
      successToaster(message);
      return true;
    } catch (e) {
      fail(e);
      return false;
    } finally {
      setBusy(null);
    }
  };

  const savePrice = async (w) => {
    if (await patch(w, { price: editing.value }, t("wines.priceUpdated"))) setEditing(null);
  };

  const off = wines.filter((w) => !w.is_available).length;
  return (
    <>
      <div className="qa-top">
        <h1 className="qa-title">{t("wines.title")}</h1>
        <input className="qa-in" placeholder={t("wines.search")} value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="qa-chips">
          {[["all", t("wines.all", { n: wines.length })], ["on", t("wines.onCard")], ["off", t("wines.hiddenCount", { n: off })]].map(([k, label]) => (
            <button key={k} className={`qa-chip ${view === k ? "on" : ""}`} onClick={() => setView(k)}>{label}</button>
          ))}
        </div>
      </div>
      <div className="qa-body">
        {shown.length === 0 && (
          <div className="qa-empty">
            {wines.length === 0 ? t("wines.empty") : t("wines.none")}
          </div>
        )}
        {shown.map((w) => (
          <div key={w.uuid} className={`qa-card ${w.is_available ? "" : "off"}`}>
            <div className="qa-name">
              {w.name}{vint(w.vintage, w.name)}
              {!w.is_available && <span className="qa-badge">{t("wines.hidden")}</span>}
              {w.status === "in_attesa" && <span className="qa-badge">{t("wines.awaiting")}</span>}
            </div>
            {w.winery && <div className="qa-sub">{w.winery}</div>}
            {editing?.uuid === w.uuid ? (
              <div className="qa-row" style={{ marginTop: 10 }}>
                <input
                  className="qa-in"
                  inputMode="decimal"
                  autoFocus
                  value={editing.value}
                  onChange={(e) => setEditing({ uuid: w.uuid, value: e.target.value })}
                  placeholder={t("wines.pricePlaceholder")}
                />
                <button className="qa-btn" disabled={busy === w.uuid || !editing.value} onClick={() => savePrice(w)}>{t("save")}</button>
                <button className="qa-btn sec" onClick={() => setEditing(null)}>X</button>
              </div>
            ) : (
              <div className="qa-row" style={{ marginTop: 10 }}>
                <button className="qa-price" onClick={() => setEditing({ uuid: w.uuid, value: String(w.price ?? "").replace(".", ",") })}>
                  {euro(w.price)} <i className="fas fa-pen" style={{ fontSize: ".75rem", marginLeft: 6 }}></i>
                </button>
                <button
                  className={`qa-btn ${w.is_available ? "red" : ""}`}
                  disabled={busy === w.uuid}
                  onClick={() =>
                    patch(w, { is_available: !w.is_available }, w.is_available ? t("wines.hiddenFromCard") : t("wines.backOnCard"))
                  }
                >
                  <i className={`fas ${w.is_available ? "fa-eye-slash" : "fa-eye"}`} style={{ marginRight: 8 }}></i>
                  {w.is_available ? t("wines.soldOut") : t("wines.show")}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

// ---- Richiesta di inserimento: il vino non e' nel dataset ----
const RequestForm = ({ initialName, onClose, onDone, onUseExisting }) => {
  const { t } = useTranslation("quickapp");
  const [f, setF] = useState({ name: initialName || "", winery: "", vintage: "", ean: "", price: "" });
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState([]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async () => {
    const text = [
      `Nome: ${f.name.trim()}`,
      f.winery.trim() && `Cantina: ${f.winery.trim()}`,
      f.vintage.trim() && `Annata: ${f.vintage.trim()}`,
    ].filter(Boolean).join(". ");
    const form = new FormData();
    form.append("price", f.price);
    form.append("text", text);
    if (f.ean.trim()) form.append("ean", f.ean.trim());
    if (front) form.append("images", front);
    if (back) form.append("images", back);
    setBusy(true);
    setExisting([]);
    try {
      await RequestWineAPI(form);
      successToaster(t("request.sent"));
      onDone();
    } catch (e) {
      // Il server risponde 202 (richiesta accettata): il client lo riceve come "risposta non standard".
      if (e?.status === 202 && e?.data?.success) {
        successToaster(e.data.message || t("request.sentApproval"));
        onDone();
      } else {
        if (e?.data?.existing?.length) setExisting(e.data.existing);
        fail(e);
      }
    } finally {
      setBusy(false);
    }
  };

  const fileField = (label, file, setFile) => (
    <label className="qa-file">
      <i className="fas fa-camera" style={{ fontSize: "1.4rem" }}></i>
      <span>
        {file ? t("request.changePhoto") : label}
        {file && <small className="d-block" style={{ display: "block" }}>{file.name}</small>}
      </span>
      <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => setFile(e.target.files?.[0] || null)} />
    </label>
  );

  return (
    <>
      <div className="qa-top">
        <h1 className="qa-title" style={{ margin: 0 }}>{t("request.title")}</h1>
      </div>
      <div className="qa-body">
        <p className="qa-sub">
          {t("request.intro")}
        </p>
        <label className="qa-lab">{t("request.name")}</label>
        <input className="qa-in" value={f.name} onChange={set("name")} />
        <label className="qa-lab">{t("request.winery")}</label>
        <input className="qa-in" value={f.winery} onChange={set("winery")} />
        <div className="qa-row" style={{ alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <label className="qa-lab">{t("request.vintage")}</label>
            <input className="qa-in" inputMode="numeric" value={f.vintage} onChange={set("vintage")} placeholder={t("request.vintagePlaceholder")} />
          </div>
          <div style={{ flex: 1 }}>
            <label className="qa-lab">{t("request.price")}</label>
            <input className="qa-in" inputMode="decimal" value={f.price} onChange={set("price")} placeholder={t("request.pricePlaceholder")} />
          </div>
        </div>
        <label className="qa-lab">{t("request.ean")}</label>
        <input className="qa-in" inputMode="numeric" value={f.ean} onChange={set("ean")} />
        <label className="qa-lab">{t("request.photos")}</label>
        <div style={{ display: "grid", gap: 10 }}>
          {fileField(t("request.labelFront"), front, setFront)}
          {fileField(t("request.labelBack"), back, setBack)}
        </div>

        {existing.length > 0 && (
          <div className="qa-card" style={{ marginTop: 14 }}>
            <div className="qa-name">{t("request.exists")}</div>
            {existing.map((w) => (
              <button key={w.uuid} className="qa-btn sec" style={{ width: "100%", marginTop: 8 }} onClick={() => onUseExisting(w)}>
                {t("request.use", { name: `${w.name}${vint(w.vintage, w.name)}` })}
              </button>
            ))}
          </div>
        )}

        <div className="qa-row" style={{ marginTop: 16 }}>
          <button className="qa-btn sec" style={{ flex: 1 }} disabled={busy} onClick={onClose}>{t("cancel")}</button>
          <button className="qa-btn" style={{ flex: 2 }} disabled={busy || f.name.trim().length < 2 || !f.price} onClick={submit}>
            {busy ? t("request.sending") : t("request.submit")}
          </button>
        </div>
      </div>
    </>
  );
};

// ---- Aggiungi un vino dal dataset ----
const AddTab = ({ onAdded }) => {
  const { t } = useTranslation("quickapp");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pick, setPick] = useState(null); // { wine, price }
  const [saving, setSaving] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await SearchDatasetWinesAPI(q.trim());
        setResults(res?.data?.wines || []);
      } catch (e) {
        fail(e);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const add = async () => {
    setSaving(true);
    try {
      await AddMyWineAPI({ wine_dataset: pick.wine.uuid, price: pick.price });
      successToaster(t("add.added"));
      setPick(null);
      setQ("");
      setResults([]);
      onAdded();
    } catch (e) {
      fail(e);
    } finally {
      setSaving(false);
    }
  };

  if (requesting) {
    return (
      <RequestForm
        initialName={q}
        onClose={() => setRequesting(false)}
        onDone={() => {
          setRequesting(false);
          onAdded();
        }}
        onUseExisting={(w) => {
          setRequesting(false);
          setQ(w.name);
          setPick({ wine: w, price: "" });
        }}
      />
    );
  }

  return (
    <>
      <div className="qa-top">
        <h1 className="qa-title">{t("add.title")}</h1>
        <input className="qa-in" placeholder={t("add.search")} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="qa-body">
        {q.trim().length < 2 && <div className="qa-empty">{t("add.minChars")}</div>}
        {searching && <div className="qa-empty">{t("add.searching")}</div>}
        {!searching && q.trim().length >= 2 && results.length === 0 && (
          <div className="qa-empty">{t("add.noResults")}</div>
        )}
        {q.trim().length >= 2 && !searching && (
          <button className="qa-btn sec" style={{ width: "100%", marginBottom: 12 }} onClick={() => setRequesting(true)}>
            {t("add.notFound")}
          </button>
        )}
        {results.map((w) => (
          <div key={w.uuid} className="qa-card">
            <div className="qa-name">{w.name}{vint(w.vintage, w.name)}</div>
            <div className="qa-sub">{[w.winery, w.denomination, w.region].filter(Boolean).join(" · ")}</div>
            {pick?.wine.uuid === w.uuid ? (
              <div className="qa-row" style={{ marginTop: 10 }}>
                <input
                  className="qa-in"
                  inputMode="decimal"
                  autoFocus
                  placeholder={t("add.pricePlaceholder")}
                  value={pick.price}
                  onChange={(e) => setPick({ wine: w, price: e.target.value })}
                />
                <button className="qa-btn" disabled={saving || !pick.price} onClick={add}>{saving ? "..." : t("add.add")}</button>
                <button className="qa-btn sec" disabled={saving} onClick={() => setPick(null)}>{t("cancel")}</button>
              </div>
            ) : (
              <button className="qa-btn sec" style={{ marginTop: 10, width: "100%" }} onClick={() => setPick({ wine: w, price: "" })}>
                {t("add.putOnCard")}
              </button>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

// ---- Carte: apri / nascondi ----
const CardsTab = () => {
  const { t } = useTranslation("quickapp");
  const [cards, setCards] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await GetMenuCardsAPI({ page: 1, perPage: 100, order: "position" });
        setCards(res?.data?.menu_cards || []);
      } catch (e) {
        fail(e);
        setCards([]);
      }
    })();
  }, []);

  const toggle = async (c) => {
    setBusy(c.uuid);
    try {
      const res = await UpdateMenuCardAPI(c.uuid, { is_active: !c.is_active });
      const active = res?.data?.is_active ?? !c.is_active;
      setCards((list) => list.map((x) => (x.uuid === c.uuid ? { ...x, is_active: active } : x)));
      successToaster(active ? t("cards.visible") : t("cards.hiddenToast"));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="qa-top"><h1 className="qa-title" style={{ margin: 0 }}>{t("cards.title")}</h1></div>
      <div className="qa-body">
        {cards === null && <div className="qa-empty">{t("loading")}</div>}
        {cards && cards.length === 0 && <div className="qa-empty">{t("cards.empty")}</div>}
        {(cards || []).map((c) => (
          <div key={c.uuid} className={`qa-card ${c.is_active === false ? "off" : ""}`}>
            <div className="qa-row">
              <div>
                <div className="qa-name">{c.name}</div>
                <div className="qa-sub">
                  {t("cards.wineCount", { count: c.wine_count ?? 0 })} · {c.is_active === false ? t("cards.hidden") : t("cards.visibleLabel")}
                </div>
              </div>
              <button className={`qa-btn ${c.is_active === false ? "" : "red"}`} disabled={busy === c.uuid} onClick={() => toggle(c)}>
                {c.is_active === false ? t("cards.show") : t("cards.hide")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

// ---- Accesso (stessa login del gestionale, ma resta dentro l'app) ----
const LoginScreen = ({ onDone }) => {
  const { t } = useTranslation("quickapp");
  const { fetchUser, fetchSection } = useContext(UserContextProvider);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await loginApi({ email: email.trim(), password });
      if (res?.success && res?.data?.access_token) {
        localStorage.setItem("token", res.data.access_token);
        fetchUser?.();
        fetchSection?.();
        onDone();
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="qa-login">
      <style>{STYLE}</style>
      <div className="qa-lang"><LanguageSwitch /></div>
      <form onSubmit={submit}>
        <h1>Risto Platform</h1>
        <p>{t("login.intro")}</p>
        <input
          className="qa-in"
          type="email"
          autoComplete="username"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          className="qa-in"
          type="password"
          autoComplete="current-password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button className="qa-btn" style={{ width: "100%" }} type="submit" disabled={busy || !email || !password}>
          {busy ? t("login.busy") : t("login.submit")}
        </button>
      </form>
    </div>
  );
};

const QuickApp = () => {
  const [authed, setAuthed] = useState(isAuthenticated());
  return authed ? (
    <AppShell onLogout={() => { localStorage.removeItem("token"); setAuthed(false); }} />
  ) : (
    <LoginScreen onDone={() => setAuthed(true)} />
  );
};

const AppShell = ({ onLogout }) => {
  const { t } = useTranslation("quickapp");
  const [tab, setTab] = useState("wines");
  const [wines, setWines] = useState([]);
  const [loading, setLoading] = useState(true);

  // Rende l'app installabile sulla schermata Home con la sua apertura su /app.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const previous = link?.getAttribute("href");
    if (link) link.setAttribute("href", "/manifest-app.json");
    return () => link && previous && link.setAttribute("href", previous);
  }, []);

  const reload = useCallback(async () => {
    try {
      const res = await GetMyWinesAPI();
      setWines(res?.data?.wines || []);
    } catch (e) {
      fail(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const tabs = [
    ["wines", "fa-wine-bottle", t("tabs.wines")],
    ["add", "fa-plus-circle", t("tabs.add")],
    ["cards", "fa-book-open", t("tabs.cards")],
    ["logout", "fa-sign-out-alt", t("tabs.logout")],
  ];

  return (
    <div className="qa">
      <style>{STYLE}</style>
      <div className="qa-lang"><LanguageSwitch /></div>
      {tab === "wines" && (loading ? <div className="qa-empty">{t("loading")}</div> : <WinesTab wines={wines} setWines={setWines} reload={reload} />)}
      {tab === "add" && <AddTab onAdded={() => { reload(); setTab("wines"); }} />}
      {tab === "cards" && <CardsTab />}
      <nav className="qa-tabs">
        {tabs.map(([k, icon, label]) => (
          <button key={k} className={`qa-tab ${tab === k ? "on" : ""}`} onClick={() => (k === "logout" ? onLogout() : setTab(k))}>
            <i className={`fas ${icon}`}></i>
            {label}
          </button>
        ))}
      </nav>
    </div>
  );
};

export default QuickApp;
